/**
 * Rezumatul erorilor de validare venite de la backend, pentru toast.
 *
 * De ce exista: raspunsul de la backend are un `title` scris pentru dezvoltatori
 * („Eroare la validarea datelor. Verifica datele trimise."), iar utilizatorul il
 * primea ca atare — fara sa afle CATE campuri sunt gresite si fara sa fie trimis
 * spre ele. Pe un formular cu ~30 de campuri (cererea de inscriere a scolii) asta
 * inseamna cautare oarba. Defectul T7/D6.
 *
 * Functia e pura, ca sa poata fi testata: suita ruleaza pe `environment: 'node'`,
 * fara jsdom (vezi `frontend/.claude/rules/known-issues.md`).
 */

/**
 * Numeralul romanesc cere „de" incepand de la 20, cu exceptia numerelor al caror
 * rest la 100 e intre 1 si 19: 20 de campuri, dar 101 campuri.
 */
function cereDe(n: number): boolean {
    const rest = n % 100;
    return n >= 20 && !(rest >= 1 && rest <= 19);
}

/**
 * Textul pentru toastul de esec al unui submit respins de validarea backendului.
 *
 * @param count numarul de campuri marcate cu eroare
 * @returns mesajul afisat, sau `null` daca nu e niciun camp de aratat
 *
 * @example
 * buildFieldErrorSummary(1)  // „Nu s-a putut trimite. Verifică 1 câmp marcat."
 * buildFieldErrorSummary(3)  // „Nu s-a putut trimite. Verifică 3 câmpuri marcate."
 * buildFieldErrorSummary(20) // „Nu s-a putut trimite. Verifică 20 de câmpuri marcate."
 */
export function buildFieldErrorSummary(count: number): string | null {
    if (!Number.isInteger(count) || count < 1) return null;

    if (count === 1) return 'Nu s-a putut trimite. Verifică 1 câmp marcat.';

    const cantitate = cereDe(count) ? `${count} de câmpuri` : `${count} câmpuri`;
    return `Nu s-a putut trimite. Verifică ${cantitate} marcate.`;
}


/** Ce se afiseaza dupa un submit respins, si daca mesajul trebuie sa RAMANA pe ecran. */
export interface ToastDeValidare {
    mesaj: string;
    /** `true` cand utilizatorul nu poate corecta singur: mesajul e dovada, nu instructiune. */
    persistent: boolean;
}

/**
 * Ce trimite mai departe un camp la care utilizatorul nu are acces in pagina curenta.
 *
 * `/mesaje` e accesibila PROFESOR-ului si ELEV-ului (`routes-provider.tsx`), deci e un capat de
 * drum real, nu un „contacteaza echipa" fara adresa.
 */
const INDRUMARE_CAMP_INVIZIBIL =
    'Problema nu e într-un câmp de pe această pagină — trimite-ne mesajul de mai sus din pagina Mesaje.';

/**
 * Textul complet al mesajului de esec, cand unele erori de la backend cad pe campuri care NU se
 * randeaza in formularul curent.
 *
 * 🔴 De ce exista: `buildFieldErrorSummary` singur spune „Verifica 1 camp marcat" — ceea ce e un
 * zid perfect daca acel camp nu e pe ecran. S-a intamplat pe bune (issue #14): elevul primea 400
 * pe `telefon`, un camp pe care pagina lui nu-l afiseaza deloc. I se cerea sa verifice ceva ce nu
 * exista.
 *
 * Regula: **un mesaj de eroare care nu se vede nicaieri pe pagina intra in mesajul de esec, si
 * acela nu se mai autoinchide.** Cand utilizatorul nu poate corecta nimic, mesajul isi schimba
 * functia — din instructiune devine DOVADA, ceva de citit, fotografiat sau trimis mai departe.
 * Un text care dispare in 8 secunde e recipientul gresit pentru asa ceva.
 *
 * Mesajul serverului se afiseaza **verbatim si intreg**: trunchiat, nu mai poate fi raportat sau
 * cautat, iar rescris in frontend produce o vanatoare de fantome cand cineva cauta textul in cod.
 *
 * @param marcate numarul de campuri marcate vizibil (din DOM)
 * @param mesajeInvizibile mesajele de la backend care nu apar nicaieri in pagina
 */
export function buildFieldErrorToast(
    marcate: number,
    mesajeInvizibile: string[] = []
): ToastDeValidare | null {
    const invizibile = mesajeInvizibile.map((m) => m.trim()).filter(Boolean);
    const rezumat = buildFieldErrorSummary(marcate);

    if (invizibile.length === 0) {
        return rezumat === null ? null : { mesaj: rezumat, persistent: false };
    }

    const capat = `${invizibile.join(' ')} ${INDRUMARE_CAMP_INVIZIBIL}`;

    return {
        mesaj: rezumat === null ? `Nu s-a putut trimite. ${capat}` : `${rezumat} ${capat}`,
        persistent: true,
    };
}


/**
 * Mesajele de la backend care NU se regasesc printre cele afisate pe pagina.
 *
 * Comparatia se face pe textul mesajului, nu pe numele campului: `applyServerFieldErrors` pune
 * mesajul serverului direct in `setError`, deci daca acel camp e randat, exact acelasi text apare
 * intr-un `FormMessage`. Ce nu apare, nu e randat.
 *
 * Functie pura, ca sa poata fi testata fara DOM (suita ruleaza pe `environment: 'node'`).
 */
export function selectInvisibleMessages(
    fieldErrors: Record<string, string> | undefined,
    mesajeVizibile: string[]
): string[] {
    if (!fieldErrors) return [];

    const vizibile = new Set(mesajeVizibile.map((m) => m.trim()).filter(Boolean));
    return Object.values(fieldErrors)
        .map((m) => (typeof m === 'string' ? m.trim() : ''))
        .filter((m) => m.length > 0 && !vizibile.has(m));
}
